/**
 * Códigos eSocial — Painel admin paramétrico
 * /dp-folha/codigos-esocial
 *
 * Permite gerenciar tabelas oficiais do eSocial (Motivos Desligamento,
 * Afastamento, Categorias Trabalhador, etc.). Dropdowns de outras telas
 * (eSocial Eventos, Rescisões, SST) puxam desta tabela.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import {
  ListChecks, Plus, RefreshCw, Loader2, Edit, Trash2, RotateCcw,
  ShieldCheck, Settings, Search, DollarSign, Percent,
} from 'lucide-react';
import { invalidateEsocialCodigos } from './forms/useEsocialCodigos';
import { useConfirm } from '../../hooks/useConfirm';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function EsocialCodigosPage() {
  const { confirm, ConfirmDialog } = useConfirm();
  const [categorias, setCategorias] = useState([]);
  const [categoriaSel, setCategoriaSel] = useState('');
  const [codigos, setCodigos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showInativos, setShowInativos] = useState(false);
  const [editing, setEditing] = useState(null); // {mode: 'create'|'edit', data}

  // ---- load categorias ---------------------------------------------------
  const loadCategorias = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/esocial-codigos/categorias`, auth());
      setCategorias(r.data || []);
      if (!categoriaSel && r.data?.length) setCategoriaSel(r.data[0].categoria);
    } catch (e) {
      toast.error('Falha ao carregar categorias');
    }
  }, [categoriaSel]);
  useEffect(() => { loadCategorias(); }, [loadCategorias]);

  // ---- load códigos da categoria ----------------------------------------
  const load = useCallback(async () => {
    if (!categoriaSel) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/esocial-codigos?categoria=${categoriaSel}&incluir_inativos=${showInativos}`,
        auth(),
      );
      setCodigos(r.data || []);
    } catch (e) {
      toast.error('Falha ao carregar códigos');
    } finally { setLoading(false); }
  }, [categoriaSel, showInativos]);
  useEffect(() => { load(); }, [load]);

  // ---- ações -------------------------------------------------------------
  const handleSave = async () => {
    if (!editing) return;
    try {
      if (editing.mode === 'create') {
        await axios.post(`${API}/api/esocial-codigos`, {
          categoria: categoriaSel,
          codigo: editing.data.codigo,
          descricao: editing.data.descricao,
          ordem: parseInt(editing.data.ordem || 0),
          dias_padrao: editing.data.dias_padrao ? parseInt(editing.data.dias_padrao) : null,
          incide_inss: editing.data.incide_inss ?? false,
          incide_irrf: editing.data.incide_irrf ?? false,
          incide_fgts: editing.data.incide_fgts ?? false,
        }, auth());
        toast.success(`Código ${editing.data.codigo} criado`);
      } else {
        const payload = {
          descricao: editing.data.descricao,
          ordem: parseInt(editing.data.ordem || 0),
          ativo: editing.data.ativo,
          dias_padrao: editing.data.dias_padrao ? parseInt(editing.data.dias_padrao) : null,
        };
        if (categoriaSel === 'MOTIVOS_AFASTAMENTO') {
          payload.incide_inss = editing.data.incide_inss ?? false;
          payload.incide_irrf = editing.data.incide_irrf ?? false;
          payload.incide_fgts = editing.data.incide_fgts ?? false;
        }
        await axios.put(`${API}/api/esocial-codigos/${editing.data.id}`, payload, auth());
        toast.success(`Código ${editing.data.codigo} atualizado`);
      }
      invalidateEsocialCodigos();
      setEditing(null);
      await Promise.all([load(), loadCategorias()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleDelete = async (cod) => {
    const ok = await confirm({
      title: cod.custom ? 'Excluir código custom?' : 'Inativar código padrão?',
      description: cod.custom
        ? `O código custom "${cod.codigo} — ${cod.descricao}" será removido permanentemente.`
        : `O código padrão "${cod.codigo} — ${cod.descricao}" será inativado (não aparecerá nos dropdowns). Você pode restaurá-lo depois clicando em "Restaurar Padrões".`,
      confirmText: cod.custom ? 'Excluir' : 'Inativar',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/esocial-codigos/${cod.id}`, auth());
      invalidateEsocialCodigos();
      toast.success(cod.custom ? 'Código removido' : 'Código inativado');
      await Promise.all([load(), loadCategorias()]);
    } catch (e) {
      toast.error('Erro ao remover');
    }
  };

  const handleRestaurarCategoria = async () => {
    const ok = await confirm({
      title: 'Restaurar padrões desta categoria?',
      description: 'Todos os códigos oficiais (não-custom) serão reativados e suas descrições corrigidas para os valores oficiais do MOS. Seus códigos custom não serão tocados.',
      confirmText: 'Restaurar',
    });
    if (!ok) return;
    try {
      const r = await axios.post(
        `${API}/api/esocial-codigos/restaurar/${categoriaSel}`, {}, auth(),
      );
      toast.success(`${r.data.reativados} reativados · ${r.data.inseridos} inseridos`);
      invalidateEsocialCodigos();
      await Promise.all([load(), loadCategorias()]);
    } catch (e) {
      toast.error('Erro ao restaurar');
    }
  };

  // ---- filtros UI -------------------------------------------------------
  const filtered = codigos.filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return c.codigo.toLowerCase().includes(q) || c.descricao.toLowerCase().includes(q);
  });

  const catInfo = categorias.find(c => c.categoria === categoriaSel);

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto" data-testid="esocial-codigos-page">
      <ConfirmDialog />
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ListChecks className="h-7 w-7 text-indigo-600" />
            Códigos eSocial
            <Badge variant="outline" className="ml-2">Tabela Paramétrica</Badge>
          </h1>
          <p className="text-sm text-slate-500">
            Códigos oficiais do MOS eSocial v_S_01_03_00. Adicione códigos custom ou ajuste descrições conforme sua necessidade — usados em toda a UI (eSocial, Rescisões, SST).
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="esocial-cod-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Categoria</CardTitle>
          <CardDescription>Selecione a tabela que deseja gerenciar.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={categoriaSel} onValueChange={setCategoriaSel}>
            <SelectTrigger className="w-full" data-testid="esocial-cod-categoria-select"><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent className="max-h-80">
              {categorias.map(c => (
                <SelectItem key={c.categoria} value={c.categoria}>
                  {c.label} ({c.ativos}/{c.total})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {catInfo && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
              <Badge variant="outline" className="bg-slate-50">{catInfo.total} total</Badge>
              <Badge variant="outline" className="bg-green-50 text-green-700">{catInfo.ativos} ativos</Badge>
              <Badge variant="outline" className="bg-indigo-50 text-indigo-700">{catInfo.custom} custom</Badge>
              <Badge variant="outline" className="bg-slate-50">{catInfo.default_count} defaults oficiais</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle>Códigos</CardTitle>
            <CardDescription>
              <span className="inline-flex items-center gap-1 mr-2"><ShieldCheck className="h-3 w-3 text-green-600" /> Padrão</span>
              <span className="inline-flex items-center gap-1"><Settings className="h-3 w-3 text-indigo-600" /> Custom (criado por você)</span>
            </CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..."
                     className="w-56 pl-8" data-testid="esocial-cod-search" />
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowInativos(s => !s)}
                    data-testid="esocial-cod-toggle-inativos">
              {showInativos ? 'Ocultar inativos' : 'Mostrar inativos'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleRestaurarCategoria}
                    data-testid="esocial-cod-restaurar">
              <RotateCcw className="h-4 w-4 mr-1" /> Restaurar padrões
            </Button>
            <Button size="sm" onClick={() => setEditing({ mode: 'create', data: { codigo: '', descricao: '', ordem: 999 } })}
                    data-testid="esocial-cod-novo">
              <Plus className="h-4 w-4 mr-1" /> Novo código
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                  <tr>
                    <th className="text-left p-2 w-16">Código</th>
                    <th className="text-left p-2">Descrição</th>
                    <th className="text-left p-2 w-24">Dias Padrão</th>
                    <th className="text-center p-2 w-24">Tipo</th>
                    {categoriaSel === 'MOTIVOS_AFASTAMENTO' && (
                      <th className="text-center p-2 w-44">Incidência</th>
                    )}
                    <th className="text-center p-2 w-20">Status</th>
                    <th className="text-right p-2 w-32">Ações</th>
                  </tr>
                </thead>
                <tbody>
                    {filtered.length === 0 && (
                      <tr><td colSpan={categoriaSel === 'MOTIVOS_AFASTAMENTO' ? 7 : 6} className="text-center text-slate-400 py-6">Nenhum código.</td></tr>
                    )}
                  {filtered.map(c => (
                    <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`esocial-cod-row-${c.codigo}`}>
                        <td className="p-2 font-mono">{c.codigo}</td>
                        <td className="p-2">{c.descricao}</td>
                        <td className="p-2 text-center">
                          {c.dias_padrao != null
                            ? <span className="font-medium">{c.dias_padrao}d</span>
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-2 text-center">
                          {c.custom
                            ? <Badge variant="outline" className="bg-indigo-50 text-indigo-700 text-[10px]">Custom</Badge>
                            : <Badge variant="outline" className="bg-green-50 text-green-700 text-[10px]">Padrão</Badge>}
                        </td>
                        {categoriaSel === 'MOTIVOS_AFASTAMENTO' && (
                          <td className="p-2 text-center">
                            <div className="flex items-center justify-center gap-1.5 text-[11px]">
                              {c.incide_inss
                                ? <span className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200 font-medium" title="Incide INSS">INSS</span>
                                : <span className="px-1.5 py-0.5 rounded text-slate-300 border border-transparent">INSS</span>}
                              {c.incide_irrf
                                ? <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 font-medium" title="Incide IRRF">IRRF</span>
                                : <span className="px-1.5 py-0.5 rounded text-slate-300 border border-transparent">IRRF</span>}
                              {c.incide_fgts
                                ? <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-medium" title="Incide FGTS">FGTS</span>
                                : <span className="px-1.5 py-0.5 rounded text-slate-300 border border-transparent">FGTS</span>}
                            </div>
                          </td>
                        )}
                        <td className="p-2">
                          {c.ativo
                            ? <Badge variant="outline" className="bg-green-50 text-green-700 text-[10px]">Ativo</Badge>
                            : <Badge variant="outline" className="bg-slate-100 text-slate-500 text-[10px]">Inativo</Badge>}
                        </td>
                        <td className="p-2 text-right">
                        <Button size="sm" variant="ghost"
                                onClick={() => setEditing({ mode: 'edit', data: { ...c } })}
                                data-testid={`esocial-cod-edit-${c.codigo}`}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600"
                                onClick={() => handleDelete(c)}
                                data-testid={`esocial-cod-del-${c.codigo}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Edit/Create */}
      <Dialog open={!!editing} onOpenChange={v => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing?.mode === 'create' ? 'Novo Código' : `Editar ${editing?.data?.codigo}`}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Código *</Label>
                <Input value={editing.data.codigo}
                       onChange={e => setEditing({ ...editing, data: { ...editing.data, codigo: e.target.value } })}
                       disabled={editing.mode === 'edit'}
                       data-testid="esocial-cod-form-codigo"
                       placeholder="ex.: 99" />
              </div>
              <div>
                <Label>Descrição *</Label>
                <Input value={editing.data.descricao}
                       onChange={e => setEditing({ ...editing, data: { ...editing.data, descricao: e.target.value } })}
                       data-testid="esocial-cod-form-descricao" />
              </div>
              <div>
                <Label>Ordem (exibição)</Label>
                <Input type="number" value={editing.data.ordem ?? 0}
                       onChange={e => setEditing({ ...editing, data: { ...editing.data, ordem: e.target.value } })} />
              </div>
              <div>
                <Label>Dias Padrão (para S-2230)</Label>
                <Input type="number" min="0" value={editing.data.dias_padrao ?? ''}
                       onChange={e => setEditing({ ...editing, data: { ...editing.data, dias_padrao: e.target.value } })}
                       placeholder="Deixe vazio se não houver padrão" />
                <p className="text-[11px] text-slate-500 mt-1">Ao selecionar este motivo no S-2230, a data final será calculada automaticamente com base nesses dias.</p>
              </div>
              {categoriaSel === 'MOTIVOS_AFASTAMENTO' && (
                <div>
                  <Label className="mb-2 block">Incidência na Folha</Label>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={!!editing.data.incide_inss}
                                onCheckedChange={v => setEditing({ ...editing, data: { ...editing.data, incide_inss: !!v } })} />
                      <span className="text-orange-700 font-medium">INSS</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={!!editing.data.incide_irrf}
                                onCheckedChange={v => setEditing({ ...editing, data: { ...editing.data, incide_irrf: !!v } })} />
                      <span className="text-violet-700 font-medium">IRRF</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={!!editing.data.incide_fgts}
                                onCheckedChange={v => setEditing({ ...editing, data: { ...editing.data, incide_fgts: !!v } })} />
                      <span className="text-blue-700 font-medium">FGTS</span>
                    </label>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Se marcado, o imposto será calculado sobre o salário integral mesmo durante o afastamento.
                  </p>
                </div>
              )}
              {editing.mode === 'edit' && (
                <div>
                  <Label>Status</Label>
                  <Select value={editing.data.ativo ? '1' : '0'}
                          onValueChange={v => setEditing({ ...editing, data: { ...editing.data, ativo: v === '1' } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Ativo</SelectItem>
                      <SelectItem value="0">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={handleSave} data-testid="esocial-cod-form-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
